import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import clsx from 'clsx';
import {
  $getRoot,
  CLEAR_EDITOR_COMMAND,
  COMMAND_PRIORITY_HIGH,
  EditorState,
  KEY_ENTER_COMMAND,
  LexicalEditor,
} from 'lexical';
import { observer } from 'mobx-react';
import * as React from 'react';
import ChatMessageStore from '../../../../ChatMessageStore';
import ChatReplyStore from '../../../../ChatReplyStore';
import { TPlainTextKey, TranslationsContext, translation } from '../../../../Translations';
import * as editorStyle from './editor.module.css';
import { exportChatHtml } from './exportChatHtml';

interface Props {
  placeholder: TPlainTextKey;
  submitChatMessage: () => void;
  store: ChatMessageStore | ChatReplyStore;
  readOnly?: boolean;
  contentClassName?: string;
  placeholderClassName?: string;
}

const SubmitOnEnterPlugin: React.FC<{ submitChatMessage: () => void }> = ({
  submitChatMessage,
}) => {
  const [editor] = useLexicalComposerContext();

  React.useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (event !== null && !event.shiftKey && !event.ctrlKey) {
            event.preventDefault();
            submitChatMessage();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor, submitChatMessage],
  );

  return null;
};

const EditablePlugin: React.FC<{ editable: boolean }> = ({ editable }) => {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
};

// The stores are reset to an empty message once a message finishes sending;
// clear the editor contents to match. A user edit can never trigger this,
// because typing keeps the store snapshot identical to the editor contents.
const ClearOnResetPlugin: React.FC<{ store: ChatMessageStore | ChatReplyStore }> = observer(
  ({ store }) => {
    const [editor] = useLexicalComposerContext();
    const storeIsEmpty = store.message.map((message) => message.text === '').getOrElseValue(true);

    React.useEffect(() => {
      if (storeIsEmpty) {
        const editorIsEmpty = editor
          .getEditorState()
          .read(() => $getRoot().getTextContent() === '');
        if (!editorIsEmpty) {
          editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
        }
      }
    }, [editor, storeIsEmpty]);

    return null;
  },
);

const ChatEditor: React.FC<Props> = ({
  placeholder,
  submitChatMessage,
  store,
  readOnly,
  contentClassName,
  placeholderClassName,
}) => {
  const onChange = (editorState: EditorState, editor: LexicalEditor): void => {
    editorState.read(() => {
      store.setEditorMessage({
        html: exportChatHtml(editor),
        text: $getRoot().getTextContent(),
      });
    });
  };

  return (
    <TranslationsContext.Consumer>
      {(ts) => (
        <div className={editorStyle.editorContainer}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={clsx(editorStyle.editorContent, contentClassName)}
                aria-label={translation('New chat message')(ts)}
                aria-placeholder={translation(placeholder)(ts)}
                placeholder={
                  <div className={clsx(editorStyle.editorPlaceholder, placeholderClassName)}>
                    {translation(placeholder)(ts)}
                  </div>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={onChange} />
          <ListPlugin />
          <HistoryPlugin />
          <ClearEditorPlugin />
          <SubmitOnEnterPlugin submitChatMessage={submitChatMessage} />
          <EditablePlugin editable={!readOnly} />
          <ClearOnResetPlugin store={store} />
        </div>
      )}
    </TranslationsContext.Consumer>
  );
};

export default ChatEditor;
